import { useState, useRef, useEffect } from 'react';
import { Menu, Bell, LogOut, User, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { roleLabels } from '../../lib/routes';
import { cn } from '../../utils/cn';

interface NavbarProps {
  onMenuClick: () => void;
}

export default function Navbar({ onMenuClick }: NavbarProps) {
  const { user, signOut } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtener iniciales del nombre
  const getInitials = (nombre: string) => {
    return nombre
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Obtener label del rol
  const getRoleLabel = (rol: string) => {
    return roleLabels[rol as keyof typeof roleLabels] || rol;
  };

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await signOut();
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Lado izquierdo */}
        <div className="flex items-center gap-4">
          {/* Botón hamburguesa móvil */}
          <button
            className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
            onClick={onMenuClick}
            aria-label="Abrir menú"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          {/* Breadcrumb o título de página podría ir aquí */}
        </div>

        {/* Lado derecho */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Botón notificaciones */}
          <button
            className="relative p-2 rounded-lg hover:bg-gray-100"
            aria-label="Notificaciones"
          >
            <Bell className="w-5 h-5 text-gray-600" />
            {/* Badge de notificaciones */}
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
          </button>

          {/* Dropdown de usuario */}
          <div className="relative" ref={dropdownRef}>
            <button
              className={cn(
                'flex items-center gap-2 sm:gap-3 p-2 rounded-lg transition-colors',
                isDropdownOpen ? 'bg-gray-100' : 'hover:bg-gray-100'
              )}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
            >
              {/* Avatar */}
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-700">
                  {user ? getInitials(user.nombre) : '?'}
                </span>
              </div>

              {/* Info usuario (oculto en móvil) */}
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-gray-900">
                  {user?.nombre || 'Usuario'}
                </p>
                <p className="text-xs text-gray-500">
                  {user ? getRoleLabel(user.rol) : ''}
                </p>
              </div>

              <ChevronDown
                className={cn(
                  'w-4 h-4 text-gray-400 hidden sm:block transition-transform',
                  isDropdownOpen && 'rotate-180'
                )}
              />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
                {/* Info usuario móvil */}
                <div className="px-4 py-3 border-b border-gray-100 sm:hidden">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.nombre}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user ? getRoleLabel(user.rol) : ''}
                  </p>
                </div>

                {/* Opciones */}
                <div className="py-1">
                  <button
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <User className="w-4 h-4" />
                    Mi perfil
                  </button>
                </div>

                <div className="border-t border-gray-100 py-1">
                  <button
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
